import { supabase } from '../lib/supabase';

/**
 * Increment AR use count for a dish
 */
export const trackARUse = async (dishId, dishName) => {
  try {
    const { error } = await supabase?.rpc('increment_ar_use', {
      p_dish_id: String(dishId),
      p_dish_name: dishName || 'Unknown Dish',
    });
    if (error) console.warn('AR tracking error:', error?.message);
  } catch (err) {
    console.warn('AR tracking failed:', err?.message);
  }
};

/**
 * Increment QR scan count for a dish
 */
export const trackQRScan = async (dishId, dishName) => {
  if (!supabase) return;
  try {
    const { error } = await supabase?.rpc('increment_qr_scan', {
      p_dish_id: String(dishId),
      p_dish_name: dishName || 'Unknown Dish',
    });
    if (error) console.warn('QR scan tracking error:', error?.message);
  } catch (err) {
    console.warn('QR scan tracking failed:', err?.message);
  }
};

const BASELINE_KEY = 'dish_stats_month_baseline_v3';
const PREV_MONTH_KEY = 'dish_stats_prev_month_totals_v3';
const EMPTY_TOTALS = { totalAR: 0, totalQR: 0, popular: 0, arChange: null, qrChange: null, popularChange: null };

const getMonthKey = (date) => {
  const d = date || new Date();
  return `${d?.getFullYear()}-${String(d?.getMonth() + 1)?.padStart(2, '0')}`;
};

const readJSON = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return null;
};

const writeJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {}
};

const calcPercent = (current, base) => {
  if (base === null || base === undefined) return null;
  if (base === 0) {
    if (current === 0) return { value: '0%', type: 'neutral' };
    return { value: '+100%', type: 'positive' };
  }
  const diff = current - base;
  const pct = Math.round((diff / base) * 100);
  if (pct === 0) return { value: '0%', type: 'neutral' };
  return {
    value: pct > 0 ? `+${pct}%` : `${pct}%`,
    type: pct > 0 ? 'positive' : 'negative',
  };
};

const computeTotalsFromRows = (rows) => {
  const cumulativeAR = rows?.reduce((sum, row) => sum + (row?.ar_uses || 0), 0);
  const restaurantRow = rows?.find(row => row?.dish_id === 'restaurant');
  const cumulativeQR = restaurantRow?.qr_scans || 0;
  const cumulativePopular = rows?.filter(d => (d?.ar_uses || 0) > 0)?.length;

  const currentMonthKey = getMonthKey();
  const baseline = readJSON(BASELINE_KEY);
  const prevMonth = readJSON(PREV_MONTH_KEY);

  if (!baseline) {
    writeJSON(BASELINE_KEY, {
      month: currentMonthKey,
      ar: cumulativeAR,
      qr: cumulativeQR,
      popular: cumulativePopular,
    });
  } else if (baseline?.month !== currentMonthKey) {
    const prevMonthAR = cumulativeAR - (baseline?.ar ?? 0);
    const prevMonthQR = cumulativeQR - (baseline?.qr ?? 0);
    const prevMonthPopular = cumulativePopular - (baseline?.popular ?? 0);

    writeJSON(PREV_MONTH_KEY, {
      month: baseline?.month,
      ar: Math.max(0, prevMonthAR),
      qr: Math.max(0, prevMonthQR),
      popular: Math.max(0, prevMonthPopular),
    });

    writeJSON(BASELINE_KEY, {
      month: currentMonthKey,
      ar: cumulativeAR,
      qr: cumulativeQR,
      popular: cumulativePopular,
    });
  }

  const activeBaseline = readJSON(BASELINE_KEY);
  const baseAR = activeBaseline?.ar ?? cumulativeAR;
  const baseQR = activeBaseline?.qr ?? cumulativeQR;
  const basePopular = activeBaseline?.popular ?? cumulativePopular;

  const totalAR = Math.max(0, cumulativeAR - baseAR);
  const totalQR = Math.max(0, cumulativeQR - baseQR);
  const popular = Math.max(0, cumulativePopular - basePopular);

  const arChange = prevMonth ? calcPercent(totalAR, prevMonth?.ar) : null;
  const qrChange = prevMonth ? calcPercent(totalQR, prevMonth?.qr) : null;
  const popularChange = prevMonth ? calcPercent(popular, prevMonth?.popular) : null;

  return { totalAR, totalQR, popular, arChange, qrChange, popularChange };
};

let dashboardStatsInflight = null;
let dashboardStatsCache = null;

/**
 * Single dish_stats fetch for the admin dashboard (chart + summary cards).
 * @param {{ force?: boolean }} options
 */
export const fetchDashboardDishStats = async (options = {}) => {
  if (!supabase) return { rows: [], totals: EMPTY_TOTALS };
  const { force = false } = options;

  if (!force && dashboardStatsCache) return dashboardStatsCache;
  if (!force && dashboardStatsInflight) return dashboardStatsInflight;

  dashboardStatsInflight = (async () => {
    const { data, error } = await supabase
      ?.from('dish_stats')
      ?.select('*')
      ?.order('ar_uses', { ascending: false });
    if (error) throw error;
    const rows = data || [];
    const totals = computeTotalsFromRows(rows);
    const result = { rows, totals };
    dashboardStatsCache = result;
    return result;
  })();

  try {
    return await dashboardStatsInflight;
  } finally {
    dashboardStatsInflight = null;
  }
};

/** @deprecated Use fetchDashboardDishStats — kept for other callers */
export const fetchDishStats = async (options = {}) => {
  const { rows } = await fetchDashboardDishStats(options);
  return rows;
};

/** @deprecated Use fetchDashboardDishStats — kept for other callers */
export const fetchTotalsWithComparison = async (options = {}) => {
  const { totals } = await fetchDashboardDishStats(options);
  return totals;
};

export const clearDashboardDishStatsCache = () => {
  dashboardStatsCache = null;
  dashboardStatsInflight = null;
};

export const fetchTotals = async () => {
  const { totals } = await fetchDashboardDishStats();
  return { totalAR: totals.totalAR, totalQR: totals.totalQR };
};
